import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User } from '../types';
import { getLoginErrorMessage, getSignupErrorMessage, getGoogleAuthErrorMessage } from '../utils/authErrorHandler';
import * as authApi from '../services/authApi';
import analytics from '../utils/analytics';

// V3 API types
interface LoginRequestV3 {
  email: string;
  password: string;
}

interface SignupRequestV3 {
  company_name: string;
  admin_first_name: string;
  admin_last_name: string;
  admin_email: string;
  admin_password: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  signupResult: { message: string; company_id: number; user_id: number } | null;
  tokenValidated: boolean;

  // Actions
  login: (credentials: LoginRequestV3) => Promise<void>;
  loginWithGoogle: (credential: string) => Promise<void>;
  signup: (data: SignupRequestV3) => Promise<{ message: string; company_id: number; user_id: number }>;
  logout: () => void;
  getCurrentUser: () => Promise<void>;
  validateToken: () => Promise<boolean>;
  handleAuthError: () => void;
  clearError: () => void;
  setLoading: (loading: boolean) => void;
  clearSignupResult: () => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      signupResult: null,
      tokenValidated: false,

      login: async (credentials: LoginRequestV3) => {
        try {
          set({ isLoading: true, error: null });

          const response = await authApi.login(credentials);

          const { access_token, refresh_token, user } = response.data;

          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', refresh_token);
          
          analytics.identify(`portal_${user.id}`, { email: user.email, role: user.role, company_id: user.company_id });

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            tokenValidated: true,
          });
        } catch (error: any) {
          // Get user-friendly error message
          const errorMessage = getLoginErrorMessage(error);
          
          set({
            error: errorMessage,
            isLoading: false,
            isAuthenticated: false,
            user: null,
          });
          throw new Error(errorMessage);
        }
      },

      loginWithGoogle: async (credential: string) => {
        try {
          set({ isLoading: true, error: null });
          
          const response = await authApi.loginWithGoogle(credential);
          
          const { access_token, refresh_token, user } = response.data;

          // Store tokens
          localStorage.setItem('access_token', access_token);
          localStorage.setItem('refresh_token', refresh_token);
          
          analytics.identify(`portal_${user.id}`, { email: user.email, role: user.role, company_id: user.company_id });

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
            tokenValidated: true,
          });
        } catch (error: any) {
          // Get user-friendly error message
          const errorMessage = getGoogleAuthErrorMessage(error);
          
          set({
            error: errorMessage,
            isLoading: false,
            isAuthenticated: false,
            user: null,
          });
          throw new Error(errorMessage);
        }
      },

      signup: async (data: SignupRequestV3) => {
        try {
          set({ isLoading: true, error: null });
          
          const response = await authApi.signup(data);
          
          // Backend returns SignupResponse with message, company_id, user_id
          // No tokens are provided - user must verify email first
          const { message, company_id, user_id } = response.data;

          set({
            signupResult: { message, company_id, user_id },
            isLoading: false,
            // User is NOT authenticated until email verification
            isAuthenticated: false,
            user: null,
          });

          return { message, company_id, user_id };
        } catch (error: any) {
          // Get user-friendly error message
          const errorMessage = getSignupErrorMessage(error);
          
          set({
            error: errorMessage,
            isLoading: false,
          });
          throw new Error(errorMessage);
        }
      },

      logout: () => {
        analytics.reset();
        authApi.logout();
        set({
          user: null,
          isAuthenticated: false,
          error: null,
          tokenValidated: false,
        });
      },

      getCurrentUser: async () => {
        try {
          set({ isLoading: true });

          const response = await authApi.getCurrentUser();
          analytics.identify(`portal_${response.data.id}`, { email: response.data.email, role: response.data.role, company_id: response.data.company_id });

          set({
            user: response.data,
            isAuthenticated: true,
            isLoading: false,
            tokenValidated: true,
            error: null,
          });
        } catch (error: any) {
          get().handleAuthError();
        }
      },

      validateToken: async () => {
        const token = localStorage.getItem('access_token');
        if (!token) {
          get().handleAuthError();
          return false;
        }

        try {
          const response = await authApi.getCurrentUser();
          set({ 
            user: response.data,
            isAuthenticated: true,
            tokenValidated: true,
            error: null 
          });
          return true;
        } catch (error: any) {
          get().handleAuthError();
          return false;
        }
      },

      handleAuthError: () => {
        // Clear all authentication data
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
        
        set({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          tokenValidated: false,
          error: 'Your session has expired. Please log in again.',
        });
      },

      clearError: () => set({ error: null }),

      setLoading: (loading: boolean) => set({ isLoading: loading }),

      clearSignupResult: () => set({ signupResult: null }),
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
